import { ValidatorFn, Validators } from '@angular/forms';
import { hasProperty, isRecord } from '@fmnts/core';

interface IPatternValidatorArgs {
  /**
   * Regular expression describing the pattern.
   * If a string is passed, the `^` character is prepended
   * and the `$` character is appended to the provided string
   * (if not already present), and the resulting regular
   * expression is used to test the values
   *
   * @link https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/pattern
   */
  pattern: string;
}

interface IRegexValidatorArgs {
  /**
   * This will create the regular expression "as is"
   * from the provided string.
   */
  regex: string;
  /**
   * Flags for the regular expression.
   * Due to the behavior of `RegExp.prototype.test` it is
   * recommended that you do not pass in flags with either
   * the global or sticky flag enabled.
   *
   * @link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/flags
   */
  flags?: string;
}

export type PatternValidatorArgs = IPatternValidatorArgs | IRegexValidatorArgs;

/**
 * Error when a `pattern` validator is invalid.
 */
export interface PatternValidationError {
  requiredPattern: string;
  actualValue: any;
}

/**
 * Type guard for checking a "pattern" validation error
 *
 * @param e Errors object
 *
 * @returns
 * `true` if the passed errors object has
 * "pattern" validation errors
 */
export const hasPatternError = hasProperty(
  'pattern',
  (u): u is PatternValidationError => isRecord(u),
);

function isRegexValidatorArgs(
  v: PatternValidatorArgs,
): v is IRegexValidatorArgs {
  return 'regex' in v && typeof v['regex'] === 'string';
}

/**
 * Creates a validation function for patters by parsing
 * the given `args`.
 *
 * @param args Pattern validation args
 *
 * @returns
 * Validator function for patterns
 */
export function patternValidator(args: PatternValidatorArgs): ValidatorFn {
  if (isRegexValidatorArgs(args)) {
    return Validators.pattern(new RegExp(args.regex, args.flags));
  }

  return Validators.pattern(args.pattern);
}
